import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { Button } from '@mehir/web-ui/button';
import { Card, CardContent } from '@mehir/web-ui/card';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The one class component in the app: React offers no hook equivalent of
 * componentDidCatch. Without it a single render error blanked the entire panel,
 * with nothing on screen and nothing in the log for whoever hit it.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ui] unhandled render error', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-lg">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <AlertOctagon className="h-12 w-12 text-destructive" />
            <div>
              <h1 className="text-xl font-semibold">Bir şeyler ters gitti</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Bu sayfa görüntülenirken beklenmeyen bir hata oluştu. Sorun sürerse teknik ekibe
                aşağıdaki mesajı iletin.
              </p>
            </div>
            <pre className="max-h-32 w-full overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
              {error.message}
            </pre>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => this.setState({ error: null })}>
                Tekrar dene
              </Button>
              <Button onClick={() => window.location.assign('/')}>Ana sayfaya dön</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
